import React, { useRef, useCallback, useLayoutEffect } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from '@tiptap/react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { v4 as uuid } from 'uuid';

// Custom Field Component
export const FieldComponent = ({ node, updateAttributes, editor, ...props }) => {
	const mainRef = useRef(null);

	const isDragging = useRef(false);
	const lastClickTime = useRef(0);
	const dragTimeout = useRef(null);
	const clickTimeout = useRef(null);

	const attribute = node?.attrs;
	const code = attribute?.code;
	const display = attribute?.display;
	const uid = attribute?.uid;
	const hide = attribute?.hide?.toLowerCase() === 'true';
	const fieldId = attribute?.fieldId;
	const isBlank = attribute?.isBlank;

	const isReadOnly = props.isReadOnly || props.extension?.options?.isReadOnly;

	useLayoutEffect(() => {
		if (!fieldId && isBlank) {
			// To update fieldId attribute if it is blank field
			const id = uuid();
			updateAttributes({ uid: id, fieldId: id });
		} else {
			updateAttributes({ uid: uuid() });
		}
		const mainwrapper = mainRef.current;
		if (mainwrapper) {
			if (hide) {
				// Add class to parent node if it is bookmark node
				mainwrapper.parentNode.classList.add('custom-field-node-bookmark');
			} else {
				const editRef = mainwrapper?.querySelector('.custom-field > span');
				// Add tabindex to editor area if it is editable node
				editRef?.setAttribute('tabindex', '0');
			}
		}
	}, []);

	const handleMouseDown = useCallback(e => {
		const fieldContainer = mainRef.current;
		isDragging.current = false;

		dragTimeout.current = setTimeout(() => {
			fieldContainer.addEventListener('mousemove', handleMouseMove);
		}, 300); // Adjust this delay as needed

		const handleMouseMove = () => {
			isDragging.current = true;
		};

		const handleMouseUp = () => {
			clearTimeout(dragTimeout.current);
			fieldContainer.removeEventListener('mousemove', handleMouseMove);
			fieldContainer.removeEventListener('mouseup', handleMouseUp);
		};

		fieldContainer.addEventListener('mouseup', handleMouseUp, { once: true });
	}, []);

	const makeActive = useCallback(
		e => {
			if (isDragging.current) {
				return;
			}

			const now = Date.now();
			const doubleClick = now - lastClickTime.current < 300; // Double-click detection

			if (doubleClick) {
				// Prevent executing onClick logic if it's a double-click
				clearTimeout(clickTimeout.current);
				return;
			}

			lastClickTime.current = now;

			// Proceed with onClick logic after a short delay to detect double-click
			clickTimeout.current = setTimeout(() => {
				const anchorPointer = editor?.state?.selection?.$anchor;
				const cursorPos = anchorPointer?.pos;
				const isFieldBorder = anchorPointer?.nodeAfter?.type?.name === 'custom-field'; // If next node is custom field
				if (isFieldBorder) {
					// Tiptap Focus if Click event found on Field Border
					editor.commands.focus(cursorPos + 1);
				} else {
					// Regular focus method
					const editableContent = mainRef.current?.querySelector('.custom-field > span');
					// This focus method used to update the editor state for click and VR dictation
					const coordinates = editor?.view?.posAtCoords({
						left: e.clientX,
						top: e.clientY,
					});
					coordinates?.pos ? editor.commands.focus(coordinates.pos) : editableContent?.focus();
				}
			}, 50);
		},
		[editor, mainRef]
	);

	const handleDoubleClick = useCallback(e => {
		// Handle double-click logic here if needed
		// Currently, we just prevent the single click action on double-click
		clearTimeout(clickTimeout.current);
	}, []);

	return (
		<NodeViewWrapper ref={mainRef} as="span" className="custom-field-wrapper" data-testid="CustomField-Wrapper">
			<Box
				component="span"
				data-type="custom-field"
				sx={
					!hide && {
						display: 'inline-flex',
					}
				}
			>
				{hide ? (
					<Tooltip role="tooltip" title={display}>
						<Box
							className="custom-field-bookmark"
							component="span"
							contentEditable={false}
							data-code={code}
							data-display={display}
							data-testid="custom-field-bookmark"
						>
							{display}
						</Box>
					</Tooltip>
				) : (
					<Box
						sx={{
							position: 'relative',
						}}
					>
						{!isBlank && (
							<Box
								className="custom-field-label"
								component="span"
								contentEditable={false}
								data-testid="custom-field-label"
								sx={{ top: '-5px', zIndex: 1 }}
								title={display}
								onClick={makeActive}
							>
								{display}
							</Box>
						)}

						<NodeViewContent
							className="custom-field"
							contentEditable={!isReadOnly}
							data-code={code}
							data-id="custom-field-input"
							data-testid="custom-field"
							data-uid={uid}
							id={`cf-${fieldId}`}
							onClick={makeActive}
							onDoubleClick={handleDoubleClick}
							onMouseDown={handleMouseDown}
						/>
					</Box>
				)}
			</Box>
		</NodeViewWrapper>
	);
};

// Custom Field Configuration
export default Node.create({
	name: 'custom-field',

	group: 'inline',

	content: 'block*',

	inline: true,

	atom: false,

	selectable: true,

	draggable: false,

	allowGapCursor: false,

	spanning: false, // mark must not span multiple nodes.

	whitespace: 'pre',

	// isolating: makes sure that the node content is treated as a separate block from surrounding content,
	// but causing issues with copy-paste - which creates custom fields and inserts copied content into them
	isolating: false,

	addAttributes() {
		return {
			code: {
				default: null,
				parseHTML: element => element.getAttribute('data-code'),
				renderHTML: attributes => {
					if (!attributes.code) {
						return {};
					}

					return {
						'data-code': attributes.code,
					};
				},
			},
			display: {
				default: 'Custom Field',
				parseHTML: element => element.getAttribute('data-display'),
				renderHTML: attributes => {
					if (!attributes.display) {
						return {};
					}

					return {
						'data-display': attributes.display,
						title: attributes.display,
					};
				},
			},
			value: {
				default: '',
				parseHTML: element => element.getAttribute('data-value'),
				renderHTML: attributes => {
					if (!attributes.value) {
						return {};
					}

					return {
						'data-value': attributes.value,
					};
				},
			},
			hide: {
				default: '',
				parseHTML: element => element.getAttribute('data-hide'),
				renderHTML: attributes => {
					if (!attributes.hide) {
						return {};
					}

					return {
						'data-hide': attributes.hide,
					};
				},
			},
			uid: {
				default: null,
				parseHTML: element => element.getAttribute('data-uid'),
				renderHTML: attributes => ({
					'data-uid': attributes.uid,
				}),
			},
			fieldId: {
				default: '',
				parseHTML: element => element.getAttribute('data-fieldId'),
				renderHTML: attributes => {
					if (!attributes.fieldId) {
						return {};
					}

					return {
						'data-fieldId': attributes.fieldId,
					};
				},
			},
			isBlank: {
				default: false,
				parseHTML: element => element.getAttribute('data-isBlank'),
				renderHTML: attributes => {
					if (!attributes.isBlank) {
						return {};
					}

					return {
						'data-isBlank': attributes.isBlank,
					};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
			{
				tag: `div[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ node, HTMLAttributes }) {
		const { display, hide } = node.attrs;
		const isHide = hide && JSON.parse(hide);

		// Render HTML for Bookmark
		if (isHide) {
			return [
				'div',
				mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
					'data-type': this.name,
					class: 'custom-field-node',
				}),
				display,
			];
		}

		//  Render HTML of Edited Content
		return [
			'div',
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
				'data-type': this.name,
				class: 'custom-field-node',
			}),
			['div', { contentId: HTMLAttributes?.['data-fieldId'] }, 0],
		];
	},

	renderText({ node }) {
		const { value, display, hide } = node.attrs;
		const isHide = hide && JSON.parse(hide);
		const textNode = isHide ? display : value;
		return textNode || '';
	},

	addOptions() {
		return {
			isReadOnly: false,
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(FieldComponent, {
			attrs: {
				'data-drag-handle': this.name,
				class: `${this.name}-node`,
			},
		});
	},
});
